import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Columns, Plus } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { recruitingAPI } from "../../services/recruitingApi";
import { SortableStageItem } from "./index";
import type { Stage } from "../../types/shared.types";
import type {
  StageTab,
  StageFormData,
  StageUsageCounts,
} from "../../types/admin.types";

// Extended Stage type with code field
interface AdminStage extends Stage {
  code?: string;
}

const StageManagementTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [jobStages, setJobStages] = useState<AdminStage[]>([]);
  const [applicationStages, setApplicationStages] = useState<AdminStage[]>([]);
  const [stageTab, setStageTab] = useState<StageTab>("job");
  const [showStageModal, setShowStageModal] = useState<boolean>(false);
  const [editingStage, setEditingStage] = useState<AdminStage | null>(null);
  const [stageForm, setStageForm] = useState<StageFormData>({
    name: "",
    code: "",
    color: "#3b82f6",
    level: "job",
  });
  const [stageUsageCounts, setStageUsageCounts] = useState<StageUsageCounts>(
    {},
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchStages();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchStages = async (): Promise<void> => {
    try {
      const [jobStagesResult, appStagesResult] = await Promise.all([
        recruitingAPI.getStages("job"),
        recruitingAPI.getStages("application"),
      ]);
      setJobStages(jobStagesResult || []);
      setApplicationStages(appStagesResult || []);

      // Fetch usage counts for all stages
      const allStages = [
        ...(jobStagesResult || []),
        ...(appStagesResult || []),
      ];
      const usageCounts: StageUsageCounts = {};
      for (const stage of allStages) {
        try {
          usageCounts[stage.id] = 0; // Placeholder
        } catch (error) {
          console.warn(
            `Failed to get usage count for stage ${stage.id}:`,
            error,
          );
          usageCounts[stage.id] = 0;
        }
      }
      setStageUsageCounts(usageCounts);
    } catch (error) {
      console.error("Error fetching stages:", error);
      const err = error as { message?: string };
      setFetchError(err.message || "Failed to load stages. Please try again.");
    }
  };

  const handleCreateStage = (): void => {
    setEditingStage(null);
    setStageForm({
      name: "",
      code: "",
      color: "#3b82f6",
      level: stageTab === "job" ? "job" : "application",
    });
    setShowStageModal(true);
  };

  const handleEditStage = (stage: AdminStage): void => {
    setEditingStage(stage);
    setStageForm({
      name: stage.name,
      code: stage.code || "",
      color: stage.color || "#3b82f6",
      level: (stage.level as StageTab) || "job",
    });
    setShowStageModal(true);
  };

  const handleSaveStage = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    try {
      if (editingStage) {
        await recruitingAPI.updateStage(editingStage.id, stageForm);
        alert(t("stages.messages.updated"));
      } else {
        await recruitingAPI.createStage(stageForm);
        alert(t("stages.messages.created"));
      }
      setShowStageModal(false);
      fetchStages();
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error saving stage: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    }
  };

  const handleDeleteStage = async (stageId: number): Promise<void> => {
    const usageCount = stageUsageCounts[stageId] || 0;

    let confirmMessage = t("stages.deleteConfirm");
    if (usageCount > 0) {
      confirmMessage = t("stages.deleteConfirmWithUsage", {
        count: usageCount,
      });
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await recruitingAPI.deleteStage(stageId);
      alert(t("stages.messages.deleted"));
      fetchStages();
    } catch (error) {
      const err = error as {
        response?: {
          data?: { error?: string; message?: string; usageCount?: number };
        };
        message: string;
      };
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message || err.message;
      const usageCountFromError = err.response?.data?.usageCount;
      if (usageCountFromError !== undefined) {
        setStageUsageCounts((prev) => ({
          ...prev,
          [stageId]: usageCountFromError,
        }));
      }
      alert("Error deleting stage: " + errorMessage);
    }
  };

  const handleReorderStages = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentStages = stageTab === "job" ? jobStages : applicationStages;
    const oldIndex = currentStages.findIndex((s) => s.id === active.id);
    const newIndex = currentStages.findIndex((s) => s.id === over.id);
    const newOrder = arrayMove(currentStages, oldIndex, newIndex).map(
      (s) => s.id,
    );

    try {
      await recruitingAPI.reorderStages(
        stageTab === "job" ? "job" : "application",
        newOrder,
      );
      fetchStages();
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error reordering stages: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
      fetchStages(); // Refresh to revert visual change
    }
  };

  if (loading) {
    return <div className="loading">{t("stages.loading")}</div>;
  }

  if (fetchError) {
    return (
      <div className="admin-section">
        <div style={{ padding: "20px", textAlign: "center", color: "#dc2626" }}>
          <p>Error loading stages: {fetchError}</p>
          <button
            className="btn-primary"
            onClick={() => {
              setFetchError(null);
              setLoading(true);
              fetchStages().finally(() => setLoading(false));
            }}
            style={{ marginTop: "10px" }}
          >
            {t("common:actions.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Columns size={16} /> {t("stages.title")}
        </h2>
        <button className="btn-primary" onClick={handleCreateStage}>
          <Plus size={16} /> {t("stages.addColumn")}
        </button>
      </div>

      <div className="kanban-columns-management">
        <div className="tabs">
          <button
            className={`tab ${stageTab === "job" ? "active" : ""}`}
            onClick={() => setStageTab("job")}
          >
            {t("stages.tabs.job")}
          </button>
          <button
            className={`tab ${stageTab === "application" ? "active" : ""}`}
            onClick={() => setStageTab("application")}
          >
            {t("stages.tabs.application")}
          </button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleReorderStages}
        >
          <SortableContext
            items={(stageTab === "job" ? jobStages : applicationStages).map(
              (s) => s.id,
            )}
            strategy={verticalListSortingStrategy}
          >
            <div className="stages-list">
              {(stageTab === "job" ? jobStages : applicationStages).map(
                (stage) => (
                  <SortableStageItem
                    key={stage.id}
                    stage={stage}
                    onEdit={handleEditStage}
                    onDelete={handleDeleteStage}
                    usageCount={stageUsageCounts[stage.id] || 0}
                  />
                ),
              )}
              {(stageTab === "job" ? jobStages : applicationStages).length ===
                0 && (
                <div className="no-stages">
                  <p>{t("stages.noStages", { type: stageTab })}</p>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Stage Create/Edit Modal */}
      {showStageModal && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowStageModal(false)}
            aria-hidden="true"
          />
          <div className="modal">
            <div className="modal-header">
              <h3>
                {editingStage
                  ? t("stages.createModal.editTitle")
                  : t("stages.createModal.title")}
              </h3>
              <button
                type="button"
                onClick={() => setShowStageModal(false)}
                aria-label="Close modal"
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveStage}>
              <div className="form-group">
                <label htmlFor="stage-name">
                  {t("stages.createModal.name")} *
                </label>
                <input
                  id="stage-name"
                  type="text"
                  value={stageForm.name}
                  onChange={(e) =>
                    setStageForm({ ...stageForm, name: e.target.value })
                  }
                  placeholder={t("stages.createModal.namePlaceholder")}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="stage-code">
                  {t("stages.createModal.code")} *
                </label>
                <input
                  id="stage-code"
                  type="text"
                  value={stageForm.code}
                  onChange={(e) => {
                    const formattedCode = e.target.value
                      .toUpperCase()
                      .replaceAll(/[^A-Z0-9_]/g, "")
                      .replaceAll(/\s+/g, "_");
                    setStageForm({ ...stageForm, code: formattedCode });
                  }}
                  placeholder={t("stages.createModal.codePlaceholder")}
                  required
                  disabled={!!editingStage}
                />
                {editingStage && (
                  <small
                    style={{
                      color: "#666",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    {t("stages.createModal.codeDisabled")}
                  </small>
                )}
                {!editingStage && (
                  <small
                    style={{
                      color: "#666",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    {t("stages.createModal.codeHelp")}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="stage-color">
                  {t("stages.createModal.color")} *
                </label>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    id="stage-color"
                    type="color"
                    value={stageForm.color}
                    onChange={(e) =>
                      setStageForm({ ...stageForm, color: e.target.value })
                    }
                    style={{ width: "60px", height: "40px", cursor: "pointer" }}
                  />
                  <input
                    type="text"
                    value={stageForm.color}
                    onChange={(e) =>
                      setStageForm({ ...stageForm, color: e.target.value })
                    }
                    placeholder="#3b82f6"
                    style={{ flex: 1 }}
                    aria-label="Color hex value"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="stage-level">
                  {t("stages.createModal.level")} *
                </label>
                <select
                  id="stage-level"
                  value={stageForm.level}
                  onChange={(e) =>
                    setStageForm({
                      ...stageForm,
                      level: e.target.value as StageTab,
                    })
                  }
                  disabled={!!editingStage}
                  required
                >
                  <option value="job">Job</option>
                  <option value="application">Application</option>
                </select>
                {editingStage && (
                  <small
                    style={{
                      color: "#666",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    {t("stages.createModal.levelDisabled")}
                  </small>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowStageModal(false)}>
                  {t("common:actions.cancel")}
                </button>
                <button type="submit" className="btn-primary">
                  {editingStage
                    ? t("stages.createModal.update")
                    : t("stages.createModal.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageManagementTab;
